/**
 * Lobby rooms for online "just play" battles (the original hot-seat barracks, new_interf.as:168-197,
 * 6844-6871, turned into a networked lobby) and level-bucketed matchmaking into such rooms.
 *
 * A room is its own mode: its host picks any PvP map (ONLINE_MAPS of src/session/online.ts, the 25 non-story battle
 * maps; the request's validator refuses any other), whatever the host's career has opened - the career lock of the
 * offline barracks screen does not apply. The room stores and sends the map by both fields (mapId and mapIndex);
 * a stored room of an older version (mapIndex only) is read with both.
 *
 * Rooms are persisted (LiveRoomDoc, collection live_rooms) at every change and restored at startup; matchmaking
 * tickets are ephemeral. Every change bumps the room's version (Room.version, carried by room.updated / room.closed).
 * Presence: a player that loses its last connection keeps its slot and ready flag for the disconnect grace period
 * (sessions.disconnectGraceMs, DISCONNECT_GRACE); a returning player keeps the slot, one that does not come back
 * leaves the lobby (sweep). After a restart every player counts as disconnected from that moment.
 *
 * A room open to a player per team (maxPlayers = teams, what the game creates) keeps a slot for every team the host
 * adds; a room created with fewer slots keeps them (its other teams are the host's).
 */
import { maskChatWords, sanitizeChatText } from '../../../shared/chat-rules';
import { randomId } from '../../../shared/random';
import { BackendError } from '../../../shared/protocol/errors';
import type { PayloadOf } from '../../../shared/protocol/messages';
import { type MatchTicket, type PlayerId, type Room, type RoomSettings, type RoomSettingsInput, type SessionInfo, defaultRoomSettings, normalizeRoomSettings } from '../../../shared/protocol/model';
import type { ListPage } from '../../../shared/protocol/messages';
import { LIST_LIMITS } from '../../../shared/protocol/validators';
import { ONLINE_MAPS } from '../../../session/online';
import type { ConnectionContext } from '../hub';
import { LiveDocWriter } from '../live-writer';
import { cursorInt, decodeCursor, encodeCursor, keysetPage, pageLimit } from '../pagination';
import { deleteOp, putOp } from '../storage';
import type { AuthService } from './auth';
import { type ServiceDeps, C } from './deps';
import type { LeaderboardService } from './leaderboard';
import type { SessionService } from './sessions';

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** The stored form of a room that is not closed (collection live_rooms, id = roomId). */
export interface LiveRoomDoc {
  format: 'fwp-live-room-v1';
  room: Room;
  savedAt: number;
}

export const LIVE_ROOM_FORMAT = 'fwp-live-room-v1';

/**
 * The settings of a request with both map fields (the validator accepted the map: roomSettingsValidator). The team
 * names are free text every player sees in the public room list (room.list) and in the room: they get the chat's
 * cleaning (sanitizeChatText: no bidirectional overrides, invisible or format characters, markup) and its word filter
 * (config.chat.words, maskChatWords), so they are no way around the chat's rules; a name that nothing is left of
 * becomes the default 'Команда #<n>'.
 */
function settingsOf(input: RoomSettingsInput, words: readonly string[]): RoomSettings {
  const settings = normalizeRoomSettings(input);
  if (!settings) throw new BackendError('BAD_REQUEST', 'The settings name no PvP map', { reason: 'invalid_payload', issues: [{ path: 'payload.settings.mapId', message: 'expected a PvP map' }] });
  const teams = settings.teams.map((t, i) => {
    const clean = sanitizeChatText(t.name);
    return { ...t, name: clean === '' ? `Команда #${i + 1}` : maskChatWords(clean, words).text };
  });
  return { ...settings, teams };
}

export class RoomService {
  private readonly rooms = new Map<string, Room>();
  private readonly byCode = new Map<string, string>();
  private readonly playerRoom = new Map<PlayerId, string>();
  private readonly chatTimes = new Map<PlayerId, number[]>();
  private readonly writer: LiveDocWriter;
  private readonly enterListeners: ((playerId: PlayerId) => void)[] = [];

  constructor(
    private readonly d: ServiceDeps,
    private readonly auth: AuthService,
    private readonly sessions: SessionService,
  ) {
    this.writer = new LiveDocWriter(d.storage, d.logger);
    sessions.onEnded((info) => this.onSessionEnded(info));
  }

  /** Called when a player creates or joins a room (matchmaking drops its tickets: one place at a time). */
  onEnter(fn: (playerId: PlayerId) => void): void {
    this.enterListeners.push(fn);
  }

  private entered(playerId: PlayerId): void {
    for (const fn of this.enterListeners) fn(playerId);
  }

  /** Rooms that are not closed (/health). */
  activeCount(): number {
    return this.rooms.size;
  }

  private graceMs(): number {
    return this.d.config.sessions.disconnectGraceMs;
  }

  /** Writes the room's live document (or removes it when the room is closed). */
  private persist(room: Room): Promise<void> {
    const id = room.roomId;
    return this.writer.schedule(`room:${id}`, () => {
      const live = this.rooms.get(id);
      if (!live || live.state === 'closed') return [deleteOp(C.liveRooms, id)];
      return [putOp(C.liveRooms, id, { format: LIVE_ROOM_FORMAT, room: live, savedAt: this.d.clock.now() } satisfies LiveRoomDoc)];
    });
  }

  /** Waits until every room change is written (flush hook: shutdown, tests). */
  async flush(): Promise<void> {
    await this.writer.idle();
  }

  /**
   * Restores the rooms of the live_rooms collection (startup, after the sessions): every player counts as
   * disconnected from now on (the grace period to come back starts); a room whose battle is no longer running returns
   * to the lobby.
   */
  async restore(): Promise<void> {
    const now = this.d.clock.now();
    let restored = 0;
    for (const doc of await this.d.storage.list<LiveRoomDoc>(C.liveRooms)) {
      const room = doc?.room;
      // A room stored by an older version names its map by mapIndex only: it is read with both fields.
      const settings = room?.settings ? normalizeRoomSettings(room.settings) : null;
      if (!doc || doc.format !== LIVE_ROOM_FORMAT || !room || typeof room.roomId !== 'string' || room.state === 'closed' || !settings) {
        this.d.logger.warn('live room document skipped', { roomId: room?.roomId ?? null });
        continue;
      }
      room.settings = settings;
      room.version = (room.version ?? 0) + 1;
      for (const p of room.players) {
        p.connected = false;
        p.disconnectedAt = now;
      }
      if (room.state === 'in_game' && (!room.sessionId || !this.sessions.isActive(room.sessionId))) {
        room.state = 'lobby';
        room.sessionId = null;
        for (const p of room.players) p.ready = false;
      }
      room.updatedAt = now;
      this.rooms.set(room.roomId, room);
      if (room.code) this.byCode.set(room.code, room.roomId);
      for (const p of room.players) this.playerRoom.set(p.playerId, room.roomId);
      void this.persist(room);
      restored++;
    }
    if (restored > 0) this.d.logger.info('rooms restored', { rooms: restored });
  }

  private code(): string {
    for (;;) {
      const b = this.d.rng.bytes(6);
      let c = '';
      for (const x of b) c += CODE_ALPHABET[x % CODE_ALPHABET.length];
      if (!this.byCode.has(c)) return c;
    }
  }

  private touch(room: Room): void {
    room.updatedAt = this.d.clock.now();
    room.version = (room.version ?? 0) + 1;
    void this.persist(room);
    this.d.hub.send(
      room.players.map((p) => p.playerId),
      'room.updated',
      { room },
    );
  }

  private require(roomId: string): Room {
    const r = this.rooms.get(roomId);
    if (!r || r.state === 'closed') throw new BackendError('NOT_FOUND', 'Room not found', { reason: 'room_not_found', roomId });
    return r;
  }

  private requireMember(room: Room, playerId: PlayerId): void {
    if (!room.players.some((p) => p.playerId === playerId)) throw new BackendError('FORBIDDEN', 'You are not in this room', { reason: 'not_in_room', roomId: room.roomId });
  }

  private requireHost(room: Room, playerId: PlayerId): void {
    if (room.hostId !== playerId) throw new BackendError('FORBIDDEN', 'Only the host can do this', { reason: 'not_host', roomId: room.roomId });
  }

  private requireLobby(room: Room): void {
    if (room.state !== 'lobby') throw new BackendError('INVALID_STATE', 'The room is not in the lobby state', { reason: 'room_not_in_lobby', roomId: room.roomId, state: room.state });
  }

  roomOf(playerId: PlayerId): Room | null {
    const id = this.playerRoom.get(playerId);
    return id ? (this.rooms.get(id) ?? null) : null;
  }

  /** Leaves the current lobby room (if any); refuses when the player is in a running battle. Synchronous (see create). */
  private leaveCurrent(playerId: PlayerId): void {
    const cur = this.roomOf(playerId);
    if (!cur) return;
    if (cur.state === 'in_game') throw new BackendError('INVALID_STATE', 'You are in a running battle', { reason: 'in_running_battle', roomId: cur.roomId });
    this.leave(playerId, cur.roomId);
  }

  private freeTeam(room: Room): number | null {
    const taken = new Set(room.players.map((p) => p.team));
    for (let t = 1; t <= room.settings.teams.length; t++) if (!taken.has(t)) return t;
    return null;
  }

  list(): Room[] {
    return [...this.rooms.values()].filter((r) => !r.private && r.state === 'lobby');
  }

  /**
   * One page of the public lobby rooms, oldest first (createdAt, then roomId): `limit` rooms after `cursor` (the
   * `next` of the previous page). Cursor `rm.<createdAt>.<roomId>`; a malformed one is BAD_REQUEST 'bad_cursor'.
   */
  page(opts: { limit?: number; cursor?: string } = {}): ListPage<Room> {
    const limit = pageLimit(opts.limit, LIST_LIMITS['room.list'].default, LIST_LIMITS['room.list'].max);
    let after: ((r: Room) => boolean) | null = null;
    if (opts.cursor !== undefined) {
      const [at, id] = decodeCursor(opts.cursor, 'rm', 2);
      const createdAt = cursorInt(at);
      after = (r) => r.createdAt > createdAt || (r.createdAt === createdAt && r.roomId > id);
    }
    const sorted = this.list().sort((a, b) => a.createdAt - b.createdAt || (a.roomId < b.roomId ? -1 : a.roomId > b.roomId ? 1 : 0));
    return keysetPage(sorted, limit, after, (r) => encodeCursor('rm', [r.createdAt, r.roomId]));
  }

  get(playerId: PlayerId, roomId: string): Room {
    const room = this.require(roomId);
    if (room.private) this.requireMember(room, playerId);
    return room;
  }

  /**
   * create / join resolve the account first and then check and change the rooms without any await in between: two
   * requests at the same moment (two joins of one free slot, two creates of one player) are applied one after the
   * other, so a room never holds more players than its slots, two players never share a team and a player is never
   * in two rooms.
   */
  async create(playerId: PlayerId, p: PayloadOf<'room.create'>): Promise<Room> {
    const settings: RoomSettings = p.settings ? settingsOf(p.settings, this.d.config.chat.words) : defaultRoomSettings();
    const a = await this.auth.requireAccount(playerId);
    this.leaveCurrent(playerId);
    const now = this.d.clock.now();
    const room: Room = {
      roomId: randomId(this.d.rng, 'room_', 8),
      code: p.private ? this.code() : null,
      hostId: playerId,
      mode: 'just',
      private: p.private ?? false,
      settings,
      players: [{ playerId, displayName: a.displayName, team: 1, ready: false, connected: true, disconnectedAt: null }],
      maxPlayers: Math.min(p.maxPlayers ?? settings.teams.length, settings.teams.length),
      state: 'lobby',
      sessionId: null,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    this.rooms.set(room.roomId, room);
    if (room.code) this.byCode.set(room.code, room.roomId);
    this.playerRoom.set(playerId, room.roomId);
    this.entered(playerId);
    await this.persist(room);
    this.d.logger.info('room created', { roomId: room.roomId, hostId: playerId, private: room.private });
    return room;
  }

  async join(playerId: PlayerId, p: PayloadOf<'room.join'>): Promise<Room> {
    const a = await this.auth.requireAccount(playerId);
    let roomId = p.roomId;
    if (p.code !== undefined) {
      const byCode = this.byCode.get(p.code);
      if (!byCode || (roomId !== undefined && roomId !== byCode)) throw new BackendError('NOT_FOUND', 'Room not found', { reason: 'room_not_found' });
      roomId = byCode;
    }
    const room = this.require(roomId!);
    if (room.players.some((x) => x.playerId === playerId)) return room;
    if (room.private && p.code === undefined) throw new BackendError('FORBIDDEN', 'This room requires a join code', { reason: 'join_code_required', roomId: room.roomId });
    this.requireLobby(room);
    if (room.players.length >= room.maxPlayers) throw new BackendError('INVALID_STATE', 'The room is full', { reason: 'room_full', roomId: room.roomId });
    const team = this.freeTeam(room);
    if (team === null) throw new BackendError('INVALID_STATE', 'The room is full', { reason: 'room_full', roomId: room.roomId });
    this.leaveCurrent(playerId);
    room.players.push({ playerId, displayName: a.displayName, team, ready: false, connected: true, disconnectedAt: null });
    this.playerRoom.set(playerId, room.roomId);
    this.entered(playerId);
    this.touch(room);
    return room;
  }

  leave(playerId: PlayerId, roomId: string): void {
    const room = this.require(roomId);
    this.requireMember(room, playerId);
    if (room.state === 'in_game') throw new BackendError('INVALID_STATE', 'Forfeit the running battle first (session.forfeit)', { reason: 'in_running_battle', roomId: room.roomId });
    room.players = room.players.filter((x) => x.playerId !== playerId);
    this.playerRoom.delete(playerId);
    if (room.players.length === 0) {
      this.close(room, 'empty');
      return;
    }
    if (room.hostId === playerId) room.hostId = room.players[0].playerId;
    for (const x of room.players) x.ready = false;
    this.touch(room);
  }

  /**
   * Account deletion (./account-deletion.ts): the player leaves every room it is in, whatever the room's state (its
   * battle was forfeited for it first: SessionService.removePlayer). A room left empty closes; the host's room gets the
   * next player as host; in a lobby the others' ready flags reset (room.updated as for room.leave). Its chat budget is
   * forgotten. Resolves once the rooms' live documents are written; returns the ids of the rooms it left.
   */
  async removePlayer(playerId: PlayerId): Promise<string[]> {
    this.chatTimes.delete(playerId);
    const left: string[] = [];
    for (const room of [...this.rooms.values()]) {
      if (!room.players.some((x) => x.playerId === playerId) && room.hostId !== playerId) continue;
      left.push(room.roomId);
      room.players = room.players.filter((x) => x.playerId !== playerId);
      if (room.players.length === 0) {
        this.close(room, 'empty');
        continue;
      }
      if (room.hostId === playerId) room.hostId = room.players[0].playerId;
      if (room.state === 'lobby') for (const x of room.players) x.ready = false;
      this.touch(room);
    }
    this.playerRoom.delete(playerId);
    await this.flush();
    return left;
  }

  /** Operator API (admin console): every room that is not closed (lobby and in game, private ones too), oldest first. */
  listAll(): Room[] {
    return [...this.rooms.values()].sort((a, b) => a.createdAt - b.createdAt || (a.roomId < b.roomId ? -1 : a.roomId > b.roomId ? 1 : 0));
  }

  /**
   * Operator API (admin console): closes a room (room.closed { reason: 'operator' } to its players). A battle that is
   * still running in it ends first without a result (SessionService.endByOperator). Returns the ended session id.
   */
  async closeByOperator(roomId: string): Promise<{ roomId: string; endedSession: string | null }> {
    const room = this.require(roomId);
    let endedSession: string | null = null;
    if (room.state === 'in_game' && room.sessionId && this.sessions.isActive(room.sessionId)) {
      endedSession = room.sessionId;
      await this.sessions.endByOperator(room.sessionId);
    }
    const live = this.rooms.get(roomId);
    if (live && live.state !== 'closed') this.close(live, 'operator');
    return { roomId, endedSession };
  }

  private close(room: Room, reason: 'empty' | 'expired' | 'operator'): void {
    const members = room.players.map((p) => p.playerId);
    room.state = 'closed';
    room.version = (room.version ?? 0) + 1;
    this.rooms.delete(room.roomId);
    if (room.code) this.byCode.delete(room.code);
    for (const pid of members) if (this.playerRoom.get(pid) === room.roomId) this.playerRoom.delete(pid);
    void this.persist(room);
    this.d.hub.send(members, 'room.closed', { roomId: room.roomId, reason, version: room.version });
    this.d.logger.info('room closed', { roomId: room.roomId, reason });
  }

  update(playerId: PlayerId, p: PayloadOf<'room.update'>): Room {
    const room = this.require(p.roomId);
    this.requireHost(room, playerId);
    this.requireLobby(room);
    const settings = settingsOf(p.settings, this.d.config.chat.words);
    if (room.players.some((x) => x.team > settings.teams.length)) {
      throw new BackendError('VALIDATION_FAILED', 'A player occupies a team that the new settings remove', { reason: 'team_occupied' });
    }
    // A room with a slot for every team keeps one for every team added; one created with fewer keeps its slots.
    const open = room.maxPlayers >= room.settings.teams.length ? settings.teams.length : room.maxPlayers;
    room.settings = settings;
    room.maxPlayers = Math.max(room.players.length, Math.min(open, settings.teams.length));
    for (const x of room.players) x.ready = false;
    this.touch(room);
    return room;
  }

  setReady(playerId: PlayerId, p: PayloadOf<'room.setReady'>): Room {
    const room = this.require(p.roomId);
    this.requireMember(room, playerId);
    this.requireLobby(room);
    room.players.find((x) => x.playerId === playerId)!.ready = p.ready;
    this.touch(room);
    return room;
  }

  setTeam(playerId: PlayerId, p: PayloadOf<'room.setTeam'>): Room {
    const room = this.require(p.roomId);
    this.requireMember(room, playerId);
    this.requireLobby(room);
    if (p.team > room.settings.teams.length) throw new BackendError('VALIDATION_FAILED', `Team ${p.team} does not exist in this room`, { reason: 'team_missing', team: p.team });
    const me = room.players.find((x) => x.playerId === playerId)!;
    if (me.team !== p.team && room.players.some((x) => x.team === p.team)) throw new BackendError('CONFLICT', `Team ${p.team} is taken`, { reason: 'team_taken', team: p.team });
    me.team = p.team;
    me.ready = false;
    this.touch(room);
    return room;
  }

  /**
   * room.chat: `vet` applies the chat's rules to the text (ChatService.vetRoomMessage: mute, guests, minimum level,
   * text check, word filter, the address's flood limit) after the membership check; the members get the text it
   * returns. Its `deliver` runs after the room's own limit, right before the message is sent: a message refused by
   * either limit counts against neither.
   */
  async chat(playerId: PlayerId, p: PayloadOf<'room.chat'>, vet: (text: string) => Promise<{ text: string; deliver: () => void }>): Promise<void> {
    this.requireMember(this.require(p.roomId), playerId);
    const { text, deliver } = await vet(p.text);
    // (the room again: it may have closed, or the player left it, while the text was checked)
    const room = this.require(p.roomId);
    this.requireMember(room, playerId);
    const now = this.d.clock.now();
    const recent = (this.chatTimes.get(playerId) ?? []).filter((t) => now - t < this.d.config.rooms.chatWindowMs);
    if (recent.length >= this.d.config.rooms.chatBurst) throw new BackendError('RATE_LIMITED', 'You are sending messages too fast', { reason: 'chat_rate_limited', retryAfterMs: Math.max(1, this.d.config.rooms.chatWindowMs - (now - recent[0])) });
    deliver();
    recent.push(now);
    this.chatTimes.set(playerId, recent);
    const me = room.players.find((x) => x.playerId === playerId)!;
    this.d.hub.send(
      room.players.map((x) => x.playerId),
      'room.chat',
      { roomId: room.roomId, playerId, displayName: me.displayName, text, at: now, version: room.version },
    );
  }

  async start(playerId: PlayerId, roomId: string): Promise<SessionInfo> {
    const room = this.require(roomId);
    this.requireHost(room, playerId);
    this.requireLobby(room);
    if (room.players.length < 2) throw new BackendError('INVALID_STATE', 'At least two players are required', { reason: 'too_few_players', players: room.players.length });
    if (!room.players.every((x) => x.ready)) throw new BackendError('INVALID_STATE', 'Not all players are ready', { reason: 'players_not_ready', players: room.players.filter((x) => !x.ready).map((x) => x.playerId) });
    if (!room.players.every((x) => x.connected)) {
      throw new BackendError('INVALID_STATE', 'A player is disconnected', { reason: 'player_disconnected', players: room.players.filter((x) => !x.connected).map((x) => x.playerId) });
    }
    // One player per team, each on a team of the settings (the online battle gives every team to one player only).
    const seen = new Set<number>();
    for (const { team } of room.players) {
      if (!Number.isInteger(team) || team < 1 || team > room.settings.teams.length) throw new BackendError('VALIDATION_FAILED', `Team ${team} does not exist in this room`, { reason: 'team_missing', team });
      if (seen.has(team)) throw new BackendError('CONFLICT', `Team ${team} has two players`, { reason: 'team_taken', team });
      seen.add(team);
    }
    room.state = 'in_game';
    const session = await this.sessions.createFromRoom(room);
    room.sessionId = session.sessionId;
    this.touch(room);
    return session;
  }

  /**
   * Room created by matchmaking: all matched players are placed in consecutive teams. A matched player that entered a
   * lobby room meanwhile leaves it (one room per player); one that is in a running battle by now fails the match
   * (INVALID_STATE 'in_running_battle', nothing is created).
   */
  async createMatched(playerIds: PlayerId[], settings: RoomSettings, confirm?: () => void): Promise<Room> {
    const players = [];
    for (let i = 0; i < playerIds.length; i++) {
      const a = await this.auth.requireAccount(playerIds[i]);
      players.push({ playerId: a.playerId, displayName: a.displayName, team: i + 1, ready: false, connected: true, disconnectedAt: null });
    }
    // No await from here on: the rooms of these players cannot change before the new room holds them. `confirm` (the
    // matchmaking: every ticket of the match is still queued) throws to drop the match.
    confirm?.();
    for (const pid of playerIds) if (this.roomOf(pid)?.state === 'in_game') throw new BackendError('INVALID_STATE', 'A matched player is in a running battle', { reason: 'in_running_battle', playerId: pid });
    for (const pid of playerIds) this.leaveCurrent(pid);
    const now = this.d.clock.now();
    const room: Room = {
      roomId: randomId(this.d.rng, 'room_', 8),
      code: this.code(),
      hostId: playerIds[0],
      mode: 'just',
      private: true,
      settings,
      players,
      maxPlayers: playerIds.length,
      state: 'lobby',
      sessionId: null,
      createdAt: now,
      updatedAt: now,
      version: 0,
    };
    this.rooms.set(room.roomId, room);
    this.byCode.set(room.code!, room.roomId);
    for (const pid of playerIds) this.playerRoom.set(pid, room.roomId);
    this.touch(room);
    // Their tickets leave the queue (one place at a time), like a room they create or join themselves.
    for (const pid of playerIds) this.entered(pid);
    return room;
  }

  private onSessionEnded(info: SessionInfo): void {
    if (!info.roomId) return;
    const room = this.rooms.get(info.roomId);
    if (!room || room.sessionId !== info.sessionId) return;
    room.state = 'lobby';
    room.sessionId = null;
    for (const x of room.players) x.ready = false;
    // Players that disconnected during the battle and did not come back within the grace period leave the room now
    // (the others keep their slot for the rest of it, see sweep).
    const now = this.d.clock.now();
    const gone = room.players.filter((x) => !x.connected && x.disconnectedAt !== null && now - x.disconnectedAt > this.graceMs()).map((x) => x.playerId);
    for (const pid of gone) {
      room.players = room.players.filter((x) => x.playerId !== pid);
      this.playerRoom.delete(pid);
    }
    if (room.players.length === 0) return this.close(room, 'empty');
    if (!room.players.some((x) => x.playerId === room.hostId)) room.hostId = room.players[0].playerId;
    this.touch(room);
  }

  /**
   * A player connected (its first connection) or lost its last one: the room shows it; a disconnected player keeps its
   * slot and ready flag for the grace period (sweep removes it from a lobby after that).
   */
  setConnected(playerId: PlayerId, connected: boolean): void {
    const room = this.roomOf(playerId);
    if (!room) return;
    const me = room.players.find((x) => x.playerId === playerId);
    if (me && me.connected !== connected) {
      me.connected = connected;
      me.disconnectedAt = connected ? null : this.d.clock.now();
      this.touch(room);
    }
  }

  sweep(now: number): void {
    const grace = this.graceMs();
    for (const room of [...this.rooms.values()]) {
      if (room.state === 'lobby') {
        for (const x of [...room.players]) {
          if (!x.connected && x.disconnectedAt !== null && now - x.disconnectedAt > grace && this.rooms.has(room.roomId)) this.leave(x.playerId, room.roomId);
        }
      }
      if (this.rooms.has(room.roomId) && room.state === 'lobby' && now - room.updatedAt > this.d.config.rooms.lobbyTtlMs) this.close(room, 'expired');
    }
  }
}

/** createMatched was refused because a ticket of the match is no longer queued (MatchmakingService.matchQueue). */
class MatchCancelled extends Error {
  constructor(readonly playerId: PlayerId) {
    super('A matched player left the queue');
  }
}

interface TicketState extends MatchTicket {
  playerId: PlayerId;
}

export class MatchmakingService {
  private readonly tickets = new Map<string, TicketState>();

  constructor(
    private readonly d: ServiceDeps,
    private readonly rooms: RoomService,
    private readonly leaderboard: LeaderboardService,
  ) {
    // A queued player that creates or joins a room leaves the queue (it could otherwise be matched into a second room).
    rooms.onEnter((playerId) => this.cancelFor(playerId));
  }

  private queueKey(t: { mode: string; teams: number; wormsPerTeam: number }): string {
    return `${t.mode}/${t.teams}/${t.wormsPerTeam}`;
  }

  /**
   * Queues the player (its earlier ticket and lobby room are dropped). The level is read first and nothing awaits
   * between dropping the old ticket and queuing the new one: two joins at the same moment leave one ticket.
   */
  async join(playerId: PlayerId, p: PayloadOf<'matchmaking.join'>, _ctx: ConnectionContext | null): Promise<MatchTicket> {
    const level = (await this.leaderboard.summaryOf(playerId))?.level ?? 1;
    const cur = this.rooms.roomOf(playerId);
    if (cur) {
      if (cur.state === 'in_game') throw new BackendError('INVALID_STATE', 'You are in a running battle', { reason: 'in_running_battle', roomId: cur.roomId });
      this.rooms.leave(playerId, cur.roomId);
    }
    this.cancelFor(playerId);
    const t: TicketState = {
      ticketId: randomId(this.d.rng, 't_', 8),
      playerId,
      mode: p.mode,
      teams: p.teams,
      wormsPerTeam: p.wormsPerTeam,
      levelBucket: Math.floor((level - 1) / this.d.config.matchmaking.levelBucketSize),
      queuedAt: this.d.clock.now(),
    };
    this.tickets.set(t.ticketId, t);
    const { playerId: _p, ...pub } = t;
    await this.tryMatch(this.queueKey(t));
    return pub;
  }

  leave(playerId: PlayerId, ticketId: string): void {
    const t = this.tickets.get(ticketId);
    if (!t || t.playerId !== playerId) throw new BackendError('NOT_FOUND', 'Ticket not found', { reason: 'ticket_not_found' });
    this.tickets.delete(ticketId);
  }

  cancelFor(playerId: PlayerId): void {
    for (const [id, t] of this.tickets) if (t.playerId === playerId) this.tickets.delete(id);
  }

  private compatible(a: TicketState, b: TicketState, now: number): boolean {
    const widen = this.d.config.matchmaking.widenAfterMs;
    if (now - a.queuedAt >= widen && now - b.queuedAt >= widen) return true;
    return a.levelBucket === b.levelBucket;
  }

  /** Whether the ticket is still queued (not left, replaced by a new join or dropped by entering a room meanwhile). */
  private queued(t: TicketState): boolean {
    return this.tickets.get(t.ticketId) === t;
  }

  /**
   * Forms the matches of one queue. Runs of one queue are serialized (a join and the sweep, or two joins at the same
   * moment, never match from one snapshot twice), and every ticket is checked to be still queued when it is picked and
   * again when the room is formed: a player that left the queue, joined again or entered a room during the awaits is
   * not put into the match (the others stay queued as they were). The room takes the matched tickets out of the queue.
   */
  private tryMatch(key: string): Promise<void> {
    return this.d.mutex.run(`matchmaking:${key}`, () => this.matchQueue(key));
  }

  private async matchQueue(key: string): Promise<void> {
    const now = this.d.clock.now();
    const queue = [...this.tickets.values()].filter((t) => this.queueKey(t) === key).sort((a, b) => a.queuedAt - b.queuedAt);
    const used = new Set<string>();
    for (const anchor of queue) {
      if (used.has(anchor.ticketId) || !this.queued(anchor)) continue;
      const group = [anchor];
      for (const t of queue) {
        if (group.length === anchor.teams) break;
        if (t === anchor || used.has(t.ticketId) || !this.queued(t) || group.some((g) => g.playerId === t.playerId)) continue;
        if (group.every((g) => this.compatible(g, t, now))) group.push(t);
      }
      if (group.length < anchor.teams) continue;
      for (const g of group) used.add(g.ticketId);
      // Any PvP map (the host of the matched room may pick another one).
      const map = ONLINE_MAPS[this.d.rng.bytes(1)[0] % ONLINE_MAPS.length];
      const settings: RoomSettings = {
        mapId: map.mapId,
        mapIndex: map.mapIndex,
        teams: group.map((_, i) => ({ worms: anchor.wormsPerTeam, color: i + 1, name: `Команда #${i + 1}` })),
      };
      let room: Room;
      try {
        room = await this.rooms.createMatched(
          group.map((g) => g.playerId),
          settings,
          () => {
            const gone = group.find((g) => !this.queued(g));
            if (gone) throw new MatchCancelled(gone.playerId);
          },
        );
      } catch (err) {
        const busy =
          err instanceof MatchCancelled
            ? { reason: 'left the queue', playerId: err.playerId }
            : err instanceof BackendError && (err.details as { reason?: string } | undefined)?.reason === 'in_running_battle'
              ? { reason: 'in a running battle', playerId: (err.details as { playerId?: string }).playerId }
              : null;
        if (busy) {
          // A matched player went into a running battle or left the queue meanwhile: the others stay queued (their
          // tickets as they were); the busy player's ticket goes.
          this.d.logger.warn('match dropped: a player is ' + busy.reason, { players: group.length });
          for (const g of group) if (g.playerId === busy.playerId) this.tickets.delete(g.ticketId);
          continue;
        }
        for (const g of group) this.tickets.delete(g.ticketId);
        throw err;
      }
      for (const g of group) this.tickets.delete(g.ticketId);
      for (const g of group) this.d.hub.send([g.playerId], 'matchmaking.matched', { ticketId: g.ticketId, roomId: room.roomId });
      this.d.logger.info('match formed', { roomId: room.roomId, players: group.length });
    }
  }

  async sweep(now: number): Promise<void> {
    for (const [id, t] of this.tickets) if (now - t.queuedAt > this.d.config.matchmaking.ticketTtlMs) this.tickets.delete(id);
    for (const key of new Set([...this.tickets.values()].map((t) => this.queueKey(t)))) await this.tryMatch(key);
  }

  size(): number {
    return this.tickets.size;
  }
}
